import { CheveronDownIcon } from '@wkda/icons';
import cx from 'classnames';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { useState } from 'react';
import type { Dropdown, NavListItem } from '../shared.types.js';

export interface VerticalNavMenuProps extends ComponentPropsWithRef<'div'> {
  LinkComponent?: any;
  navList: NavListItem[];
}

export default function VerticalNavMenu({
  navList,
  LinkComponent = 'a',
  className,
  ...spreadProps
}: VerticalNavMenuProps) {
  const [openedIndexes, setOpenedIndexes] = useState<number | null>(null);

  function toggleDropDown(itemIndex: number): void {
    setOpenedIndexes((prevIndex) => {
      if (prevIndex === itemIndex) {
        return null;
      }
      return itemIndex;
    });
  }

  return (
    <div className={cx('wa-nav-vertical', className)} {...spreadProps}>
      <ul className="wa-nav-vertical__list">
        {navList.map((item, index) => {
          const itemHasDropdowns =
            item?.dropDowns != null && item.dropDowns.length > 0;
          const isOpened = openedIndexes === index;
          return (
            <li
              className={cx('wa-nav-vertical__item', {
                'wa-accordeon__container': itemHasDropdowns,
                'wa-accordeon--is-open': isOpened,
              })}
              key={index}
            >
              {!itemHasDropdowns ? (
                <LinkComponent
                  className="wa-nav-vertical__item-link"
                  href={item.link}
                >
                  {item.icon && (
                    <span className="wa-nav-vertical__item-link-icon">
                      {item.icon}
                    </span>
                  )}
                  {item.title ? <ItemTitle {...item} /> : null}
                </LinkComponent>
              ) : (
                <div
                  onClick={() => toggleDropDown(index)}
                  className={cx('wa-nav-vertical__item-button', {
                    'wa-nav-vertical__item-button--open': isOpened,
                  })}
                >
                  {item.title ? <ItemTitle {...item} /> : null}
                  <CheveronDownIcon
                    className={cx('wa-nav-vertical__chevron', {
                      'wa-nav-vertical__chevron--is-open': isOpened,
                    })}
                  />
                </div>
              )}
              {itemHasDropdowns ? (
                <div className="wa-accordeon__content">
                  {(item.dropDowns ?? []).map(
                    function renderDropdowns(dropDown, key) {
                      return (
                        <NavSubList
                          LinkComponent={LinkComponent}
                          dropdown={dropDown}
                          key={key}
                        />
                      );
                    },
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NavSubList({
  dropdown,
  LinkComponent,
}: {
  dropdown: Dropdown;
  LinkComponent: any;
}): JSX.Element {
  const listTitle = dropdown.title ? <ItemTitle {...dropdown} /> : null;
  return (
    <ul className="wa-nav-vertical__sublist">
      {listTitle && (
        <li className="wa-nav-vertical__sublist-title">{listTitle}</li>
      )}
      {dropdown.items.map((item, key) => (
        <li key={key} className="wa-nav-vertical__subitem">
          <LinkComponent
            className="wa-nav-vertical__subitem-link"
            href={item.link}
          >
            {item.title ? <ItemTitle {...item} /> : null}
          </LinkComponent>
        </li>
      ))}
    </ul>
  );
}

export function ItemTitle({
  title,
  mobileTitle,
}: {
  title: ReactNode;
  mobileTitle?: string;
}) {
  return mobileTitle ? (
    <>
      <span className="header-item-title">{title}</span>
      <span className="header-item-title-mobile">{mobileTitle}</span>
    </>
  ) : title ? (
    <>{title}</>
  ) : null;
}
